import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { securedSupabase } from '@/integrations/supabase/secured-client';
import { useLanguage } from '@/contexts/LanguageContext';
import { getDealerID } from '@/utils/dealerDataUtils';
import { sessionManager } from '@/utils/sessionManager';
import { ga4Tracker } from '@/utils/ga4Tracking';
import { DealerManager } from '@/utils/dealerManager';
import { safeRedirect, createDealerURLConfig } from '@/utils/urlValidator';
import { inventoryRetrieval, isInventoryLoaded } from '@/utils/inventoryUtils';
import { logger } from '@/utils/logger';
import { detectAutoSkipCategory, AUTO_SKIP_TITLE_MAP } from '@/utils/salesPageDetector';

// Nested menu item structure - supports unlimited levels
export interface MenuItem {
  id: string;
  position: number;
  title: string;
  titleWordPos?: number;
  action?: string;
  icon?: string;
  children?: MenuItem[];
  messageTextOverride?: string;
  dynamicPriceRanges?: {
    enabled: boolean;
    startingPrice: number;
  };
  inventoryFilter?: {
    field: string;
    value: string;
    operator?: 'equals' | 'lessThan' | 'greaterThan' | 'between';
  };
  hideIfZeroCount?: boolean; // Optional: hide this item if inventory count is 0
}

export interface PromptMenuData {
  prompt_menu_id?: number;
  menu_name?: string;
  prompts_eng: MenuItem[];
  prompts_fr: MenuItem[];
}

// Selection tracks position at each level (supports unlimited depth)
export interface PromptMenuSelection {
  positions: number[]; // Array of positions, one per level [l1_pos, l2_pos, l3_pos, ...]
  l1Title?: string; // Track L1 title for showroom filtering
}

export interface LevelButton {
  position: number;
  title: string;
  level: number;
  icon?: string;
  clickAction?: string;
  count?: number; // Inventory count for this item
  hideIfZeroCount?: boolean; // Optional: hide this item if inventory count is 0
}

export interface ClickActionResult {
  type: 'javascript' | 'text' | 'form' | 'url' | 'continue';
  value?: string;
  shouldContinue?: boolean;
}

export const usePromptMenu = () => {
  const [menuData, setMenuData] = useState<PromptMenuData | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selection, setSelection] = useState<PromptMenuSelection>({ positions: [] });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [messageOverride, setMessageOverride] = useState<string | null>(null);
  const autoSkippedL1Ref = useRef(false);
  const { language } = useLanguage();

  const [effectiveDealerId, setEffectiveDealerId] = useState<number | null>(null);

  // Get dealer ID asynchronously
  useEffect(() => {
    const fetchDealerId = async () => {
      try {
        const dealerId = await getDealerID();
        setEffectiveDealerId(dealerId);
      } catch (error) {
        setTimeout(async () => {
          try {
            const dealerId = await getDealerID();
            setEffectiveDealerId(dealerId);
          } catch (retryError) {
            setEffectiveDealerId(null);
          }
        }, 2000);
      }
    };
    fetchDealerId();
  }, [refreshTrigger]);

  // Subscribe to real-time dealer config changes
  useEffect(() => {
    if (!effectiveDealerId) return;

    // Use a unique channel name per effect run to avoid "cannot add callbacks
    // after subscribe()" errors when an existing channel for this dealer is
    // still registered in the realtime client (StrictMode, remounts, etc.)
    const channelName = `dealer_config_${effectiveDealerId}_${Math.random().toString(36).slice(2, 8)}`;

    // Defensively remove any stale channel objects for this dealer before
    // creating a new one.
    try {
      const stale = supabase.getChannels?.() || [];
      stale
        .filter((ch: any) => typeof ch?.topic === 'string' && ch.topic.includes(`dealer_config_${effectiveDealerId}`))
        .forEach((ch: any) => {
          try { supabase.removeChannel(ch); } catch (_) { /* noop */ }
        });
    } catch (_) { /* noop */ }

    const channel = supabase.channel(channelName);
    channel
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'd2c_chatbot_dealers',
          filter: `dealer_id=eq.${effectiveDealerId}`
        },
        (payload: any) => {
          const newConfig = payload.new as any;
          const oldConfig = payload.old as any;

          logger.log('🔍 PROMPT MENU: Comparing configs:', {
            old_menu_id: oldConfig?.prompt_menu_id,
            new_menu_id: newConfig?.prompt_menu_id,
            old_active: oldConfig?.prompt_menu_is_active,
            new_active: newConfig?.prompt_menu_is_active,
          });

          logger.log('🔄 PROMPT MENU: Triggering refresh due to dealer config change');
          setRefreshTrigger(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      logger.log('👋 PROMPT MENU: Cleaning up real-time subscription');
      try { supabase.removeChannel(channel); } catch (_) { /* noop */ }
    };
  }, [effectiveDealerId]);

  // Load prompt menu data
  useEffect(() => {
    const loadPromptMenu = async () => {
      if (!effectiveDealerId) {
        setIsLoading(false);
        setIsEnabled(false);
        return;
      }

      try {
        setIsLoading(true);
        setIsEnabled(true);

        // Force refresh dealer config to get latest prompt_menu_id
        // Add cache buster to ensure fresh data
        let dealerConfig: any;
        dealerConfig = await DealerManager.forceRefresh();
        
        if (!dealerConfig || !dealerConfig.prompt_menu_is_active) {
          setIsEnabled(false);
          setIsLoading(false);
          return;
        }

        // Use the prompt_menu_id from dealer configuration (separate from system prompt_id)
        const promptMenuId = dealerConfig.prompt_menu_id || 1;

        // Call SECURITY DEFINER RPC that returns the dealer's active menu.
        // Bypasses header-gated RLS (Supabase gateway strips custom x-* headers
        // on anon-role requests, so the previous direct SELECT returned 0 rows
        // in production).
        const { data: rpcData, error: menuError } = await securedSupabase.raw
          .rpc('get_prompt_menu', { p_dealer_id: effectiveDealerId });

        if (menuError) {
          console.error('❌ PROMPT MENU: Failed to load menu:', menuError);
          setIsEnabled(false);
          setIsLoading(false);
          return;
        }

        const promptMenuData = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        if (!promptMenuData) {
          logger.warn('⚠️ PROMPT MENU: RPC returned no menu for dealer', effectiveDealerId);
          setIsEnabled(false);
          setIsLoading(false);
          return;
        }


  const transformNestedToFlat = (items: any[]): MenuItem[] => {
    const result: MenuItem[] = [];
    
    const processItem = (item: any): MenuItem => {
      // Extract messageTextOverride from text: action if present
      let messageOverride = item.messageTextOverride || '';
      let normalizedAction = item.action || 'continue';
      
      if (normalizedAction.startsWith('text:')) {
        messageOverride = normalizedAction.substring(5);
        normalizedAction = 'continue';
      }
      
      return {
        id: item.id,
        position: item.position,
        title: item.title,
        titleWordPos: item.wordPosition || item.titleWordPos || 1,
        action: normalizedAction,
        icon: item.icon,
        messageTextOverride: messageOverride,
        dynamicPriceRanges: item.dynamicPriceRanges,
        inventoryFilter: item.inventoryFilter || item.vectorStoreFilter, // Support legacy name
        children: item.children ? item.children.map(processItem) : []
      };
    };
    
    return items.filter(item => item != null).map(processItem);
  };

        const menuDataObj: PromptMenuData = {
          prompt_menu_id: promptMenuData.prompt_menu_id,
          menu_name: promptMenuData.menu_name,
          prompts_eng: transformNestedToFlat((promptMenuData.prompts_eng as any[]) || []),
          prompts_fr: transformNestedToFlat((promptMenuData.prompts_fr as any[]) || [])
        };

        setMenuData(menuDataObj);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    loadPromptMenu();
  }, [effectiveDealerId, refreshTrigger]);

  // Auto-skip L1 based on URL patterns (e.g., /new/ → skip to L2 New)
  useEffect(() => {
    if (!menuData || !isEnabled || isLoading) return;
    // Only auto-skip once per mount, and only if user hasn't navigated yet
    if (selection.positions.length > 0) return;
    
    const category = detectAutoSkipCategory();
    if (!category) return;
    
    const titles = AUTO_SKIP_TITLE_MAP[category];
    if (!titles) return;
    
    const items = language === 'fr' ? menuData.prompts_fr : menuData.prompts_eng;
    const stripAccents = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    
    const matchingItem = items.find(item => {
      const itemStripped = stripAccents(item.title);
      return titles.some(t => {
        const tStripped = stripAccents(t);
        return tStripped === itemStripped || itemStripped.startsWith(tStripped) || tStripped.startsWith(itemStripped);
      });
    });
    
    if (matchingItem) {
      logger.log(`🚀 Auto-skipping L1 to "${matchingItem.title}" based on URL pattern (${category})`);
      autoSkippedL1Ref.current = true;
      setSelection({ positions: [matchingItem.position], l1Title: matchingItem.title });
    } else {
      logger.warn(`⚠️ Auto-skip: No L1 match found for category "${category}". Titles tried:`, titles, 'Menu items:', items.map(i => i.title));
    }
  }, [menuData, isEnabled, isLoading, language]);

  // Helper to get current menu items based on language
  const getCurrentItems = useCallback((): MenuItem[] => {
    if (!menuData) return [];
    return language === 'fr' ? menuData.prompts_fr : menuData.prompts_eng;
  }, [menuData, language]);

  // Helper to navigate to a specific item in the tree
  const getItemAtPath = useCallback((positions: number[]): MenuItem | null => {
    let items = getCurrentItems();
    let current: MenuItem | null = null;

    for (let i = 0; i < positions.length; i++) {
      const position = positions[i];
      current = items.find(item => item.position === position) || null;
      if (!current) return null;
      if (i < positions.length - 1) {
        items = current.children || [];
      }
    }
    return current;
  }, [getCurrentItems]);

  // Refresh function to be called externally
  const refreshPromptMenu = useCallback(() => {
    logger.log('🔄 Refreshing prompt menu...');
    setRefreshTrigger(prev => prev + 1);
    setSelection({ positions: [] }); // Reset selection
    setMessageOverride(null);
  }, []);

  // Create a global refresh function
  useEffect(() => {
    // Attach refresh function to window for global access
    (window as any).refreshPromptMenu = refreshPromptMenu;
    
    return () => {
      // Cleanup
      delete (window as any).refreshPromptMenu;
    };
  }, [refreshPromptMenu]);

  // Secure action executor - replaces dangerous eval() usage
  const executeSecureAction = (actionCode: string): any => {
    logger.log('🔧 Executing secure action:', actionCode);
    
    // Predefined safe actions - no eval() needed
    const secureActions: { [key: string]: () => any } = {
      'refreshPromptMenu()': () => {
        logger.log('🔄 Executing refreshPromptMenu action');
        if ((window as any).refreshPromptMenu) {
          return (window as any).refreshPromptMenu();
        }
        return null;
      },
      'window.refreshPromptMenu()': () => {
        logger.log('🔄 Executing window.refreshPromptMenu action');
        if ((window as any).refreshPromptMenu) {
          return (window as any).refreshPromptMenu();
        }
        return null;
      },
      "$('#financeMultistepPopupContainer').popupize().setTrigger('click').open()": () => {
        logger.log('🔄 Executing finance popup action');
        try {
          if ((window as any).$ && (window as any).$('#financeMultistepPopupContainer').length > 0) {
            return (window as any).$('#financeMultistepPopupContainer').popupize().setTrigger('click').open();
          } else {
            logger.warn('jQuery or #financeMultistepPopupContainer not found');
            return null;
          }
        } catch (error) {
          console.error('Error executing finance popup:', error);
          return null;
        }
      },
      // Add more predefined actions as needed
    };

    // Normalize the action code (remove whitespace)
    const normalizedAction = actionCode.trim();
    
    if (secureActions[normalizedAction]) {
      try {
        const result = secureActions[normalizedAction]();
        logger.log('✅ Secure action executed successfully, result:', result);
        return result;
      } catch (error) {
        console.error('❌ Error executing secure action:', error);
        return null;
      }
    } else {
      logger.warn('🚨 SECURITY: Attempted to execute unrecognized action:', normalizedAction);
      logger.warn('🚨 Available actions:', Object.keys(secureActions));
      return null;
    }
  };

  // Process click action and return the result
  const processClickAction = (clickAction: string): ClickActionResult => {
    if (!clickAction || clickAction.trim() === '') {
      logger.log('🔍 processClickAction: Empty action, returning continue');
      return { type: 'continue', shouldContinue: true };
    }

    const trimmedAction = clickAction.trim();

    if (trimmedAction.startsWith('JS$')) {
      const jsCode = trimmedAction.substring(3);
      logger.log('✅ processClickAction: JavaScript action detected:', jsCode);
      return { type: 'javascript', value: jsCode };
    }

    // Handle both Text$ and text: formats
    if (trimmedAction.startsWith('Text$')) {
      // Extract text between quotes
      const match = trimmedAction.match(/Text\$"([^"]*)"/);
      const value = match ? match[1] : trimmedAction.substring(5);
      logger.log('✅ processClickAction: Text action (Text$) detected:', value);
      return { type: 'text', value };
    }
    
    if (trimmedAction.startsWith('text:')) {
      // Extract text after colon (used by PromptMenuManager)
      const value = trimmedAction.substring(5);
      logger.log('✅ processClickAction: Text action (text:) detected:', value);
      return { type: 'text', value };
    }

    // Handle price range actions - these should append to the message
    if (trimmedAction.startsWith('price:')) {
      const value = trimmedAction.substring(6);
      return { type: 'text', value };
    }

    // Handle BOTH Form$ and form: formats (case-insensitive)
    if (trimmedAction.toLowerCase().startsWith('form$')) {
      const value = trimmedAction.substring(5).trim();
      logger.log('✅ processClickAction: Form action (Form$) detected:', { 
        value, 
        menuId: menuData?.prompt_menu_id 
      });
      return { type: 'form', value };
    }
    
    if (trimmedAction.toLowerCase().startsWith('form:')) {
      const value = trimmedAction.substring(5).trim();
      logger.log('✅ processClickAction: Form action (form:) detected:', { 
        value, 
        menuId: menuData?.prompt_menu_id 
      });
      return { type: 'form', value };
    }

    if (trimmedAction.startsWith('URL$')) {
      const value = trimmedAction.substring(4);
      logger.log('✅ processClickAction: URL action (URL$) detected:', value);
      return { type: 'url', value };
    }
    
    if (trimmedAction.startsWith('url:')) {
      const value = trimmedAction.substring(4);
      logger.log('✅ processClickAction: URL action (url:) detected:', value);
      return { type: 'url', value };
    }

    return { type: 'continue', shouldContinue: true };
  };

  // Helper to check if an item is a leaf node (has no non-price-range children)
  const isLeafNode = (item: MenuItem): boolean => {
    if (!item.children || item.children.length === 0) return true;
    
    // Check if it has any NON-price-range children
    const hasRegularChildren = item.children.some(child => {
      const action = child.action || '';
      const isPriceRange = action.startsWith('text:') && (
        action.includes('less than') || 
        action.includes('more than') || 
        action.includes('between') ||
        action.includes('à moins de') ||
        action.includes('à plus de') ||
        action.includes('entre')
      );
      return !isPriceRange; // Return true if this is NOT a price range
    });
    
    // It's a leaf if it has NO regular children (only price ranges or nothing)
    return !hasRegularChildren;
  };

  // Check if dynamic price ranges are enabled by walking up the tree
  const hasDynamicPriceRangesEnabled = (positions: number[]): { enabled: boolean; startingPrice: number } | null => {
    let items = getCurrentItems();
    
    for (let i = 0; i < positions.length; i++) {
      const position = positions[i];
      const current = items.find(item => item.position === position);
      if (!current) return null;
      
      // Check if this item has price ranges enabled
      if (current.dynamicPriceRanges?.enabled) {
        return current.dynamicPriceRanges;
      }
      
      items = current.children || [];
    }
    
    return null;
  };

  // Get inventory count by applying cumulative filters from root to current path
  const getInventoryCountWithFilters = (positions: number[]): number => {
    if (!isInventoryLoaded()) {
      return 0;
    }
    
    try {
      const accumulatedFilters: MenuItem['inventoryFilter'][] = [];
      let items = getCurrentItems();
      
      for (let i = 0; i < positions.length; i++) {
        const position = positions[i];
        const item = items.find(it => it.position === position);
        if (!item) break;
        
        if (item.inventoryFilter) {
          accumulatedFilters.push(item.inventoryFilter);
        }
        
        items = item.children || [];
      }
      
      const criteria: any = {};
      
      for (const filter of accumulatedFilters) {
        const field = filter.field;
        const value = filter.value;
        const operator = filter.operator || 'equals';
        
        let criteriaField: string;
        if (field.toLowerCase() === 'vehicle type') {
          criteriaField = 'vehicleType';
        } else if (field.toLowerCase() === 'fuel_type' || field.toLowerCase() === 'fuel type') {
          criteriaField = 'fuelType';
        } else if (field.toLowerCase() === 'status') {
          criteriaField = 'status';
        } else {
          criteriaField = field;
        }
        
        if (field.toLowerCase() === 'final price' || field.toLowerCase() === 'price') {
          if (operator === 'lessThan') {
            criteria.maxPrice = parseFloat(value);
          } else if (operator === 'greaterThan') {
            criteria.minPrice = parseFloat(value);
          } else if (operator === 'between') {
            const [min, max] = value.split(',').map(v => parseFloat(v.trim()));
            criteria.minPrice = min;
            criteria.maxPrice = max;
          }
          continue;
        }
        
        if (operator === 'equals') {
          criteria[criteriaField] = value;
        }
      }
      
      const vehicles = inventoryRetrieval(criteria);
      return vehicles.length;
      
    } catch (error) {
      return 0;
    }
  };

  const getInventoryCountForItem = (item: MenuItem, parentPositions: number[]): number => {
    if (!isInventoryLoaded()) return 0;
    
    if (item.position >= 1000) {
      try {
        const accumulatedFilters: MenuItem['inventoryFilter'][] = [];
        let items = getCurrentItems();
        
        for (let i = 0; i < parentPositions.length; i++) {
          const position = parentPositions[i];
          const parentItem = items.find(it => it.position === position);
          if (!parentItem) break;
          
          if (parentItem.inventoryFilter) {
            accumulatedFilters.push(parentItem.inventoryFilter);
          }
          
          items = parentItem.children || [];
        }
        
        if (item.inventoryFilter) {
          accumulatedFilters.push(item.inventoryFilter);
        }
        
        const criteria: any = {};
        
        for (const filter of accumulatedFilters) {
          const field = filter.field;
          const value = filter.value;
          const operator = filter.operator || 'equals';
          
          let criteriaField: string;
          if (field.toLowerCase() === 'vehicle type') {
            criteriaField = 'vehicleType';
          } else if (field.toLowerCase() === 'fuel_type' || field.toLowerCase() === 'fuel type') {
            criteriaField = 'fuelType';
          } else if (field.toLowerCase() === 'status') {
            criteriaField = 'status';
          } else {
            criteriaField = field;
          }
          
          if (field.toLowerCase() === 'final price' || field.toLowerCase() === 'price') {
            if (operator === 'lessThan') {
              criteria.maxPrice = parseFloat(value);
            } else if (operator === 'greaterThan') {
              criteria.minPrice = parseFloat(value);
            } else if (operator === 'between') {
              const [min, max] = value.split(',').map(v => parseFloat(v.trim()));
              criteria.minPrice = min;
              criteria.maxPrice = max;
            }
            continue;
          }
          
          if (operator === 'equals') {
            criteria[criteriaField] = value;
          }
        }
        
        const vehicles = inventoryRetrieval(criteria);
        return vehicles.length;
        
      } catch (error) {
        return 0;
      }
    }
    
    const fullPath = [...parentPositions, item.position];
    return getInventoryCountWithFilters(fullPath);
  };

  // Get available buttons for current level
  const getCurrentLevelItems = (): LevelButton[] => {
    if (!menuData) return [];

    const currentLevel = selection.positions.length + 1;
    
    // If at root level, return all top-level items (never show price ranges here)
    if (selection.positions.length === 0) {
      const items = getCurrentItems();
      const buttons = items
        .filter(item => item != null)
        .map(item => ({
          position: item.position,
          title: item.title,
          level: 1,
          icon: item.icon || 'fa-circle',
          clickAction: item.action || '',
          count: item.inventoryFilter ? getInventoryCountForItem(item, []) : undefined,
          hideIfZeroCount: item.hideIfZeroCount // Pass through the per-item setting
        }))
        .sort((a, b) => a.position - b.position);
      
      // Filter out items with zero count if their hideIfZeroCount flag is true
      const filtered = buttons.filter(btn => {
        if (btn.hideIfZeroCount === true && btn.count !== undefined && btn.count === 0) {
          return false;
        }
        return true;
      });
      return filtered;
    }

    // Otherwise, get children of the currently selected item
    const parentItem = getItemAtPath(selection.positions);
    if (!parentItem || !parentItem.children) return [];

    // Check if dynamic price ranges are enabled in the tree
    const priceRangeConfig = hasDynamicPriceRangesEnabled(selection.positions);

    // Filter children: if dynamic price ranges are enabled, hide ALL existing price range items
    let childrenToShow = parentItem.children.filter(child => {
      // Check if this is a price range item by action
      const isPriceRangeByAction = child.action?.startsWith('text:') && (
        child.action.includes('less than') || 
        child.action.includes('more than') || 
        child.action.includes('between') ||
        child.action.includes('à moins de') ||
        child.action.includes('à plus de') ||
        child.action.includes('entre')
      );
      
      // Check if this is a price range item by title pattern (e.g., "$40K-$50K", "<$30K", ">$60K")
      const isPriceRangeByTitle = /[\$€£][\d,]+K?[-<>]|[<>][\$€£]/.test(child.title);
      
      const isPriceRange = isPriceRangeByAction || isPriceRangeByTitle;
      
      // If dynamic price ranges are enabled and this is a price range, hide it
      if (priceRangeConfig && isPriceRange) {
        return false;
      }
      
      // Show all non-price-range items
      return true;
    });

    // Check if we should show price ranges based on the FILTERED children
    const hasOnlyPriceRangesOrEmpty = childrenToShow.length === 0 || 
      childrenToShow.every(child => {
        const isPriceRange = child.action?.startsWith('price:') || 
          /[\$€£][\d,]+K?[-<>]|[<>][\$€£]/.test(child.title);
        return isPriceRange;
      });

    // Dynamically add price ranges if enabled and we have no non-price-range children
    if (hasOnlyPriceRangesOrEmpty && priceRangeConfig && !childrenToShow.some(child =>
      child.action?.startsWith('text:') && (
        child.action.includes('less than') || 
        child.action.includes('more than') || 
        child.action.includes('between') ||
        child.action.includes('à moins de') ||
        child.action.includes('à plus de') ||
        child.action.includes('entre')
      )
    )) {
      const startPrice = priceRangeConfig.startingPrice;
      const priceRanges: MenuItem[] = [];
      
      for (let i = 0; i < 6; i++) {
        // For i=0: lower = startPrice (for "<$20K")
        // For i>=1: lower = startPrice + ((i-1) * 10000) to start ranges from the base price
        const lower = i === 0 ? startPrice : startPrice + ((i - 1) * 10000);
        const upper = lower + 10000;
        
        let title: string;
        let action: string;
        let inventoryFilter: MenuItem['inventoryFilter'];
        
        if (i === 0) {
          title = language === 'fr' ? `<${(lower / 1000).toFixed(0)}K$` : `<$${(lower / 1000).toFixed(0)}K`;
          action = language === 'fr' ? `price:de moins de ${lower.toLocaleString()} $` : `price:under $${lower.toLocaleString()}`;
          inventoryFilter = {
            field: 'Final Price',
            value: lower.toString(),
            operator: 'lessThan'
          };
        } else if (i === 5) {
          title = language === 'fr' ? `>${(lower / 1000).toFixed(0)}K$` : `>$${(lower / 1000).toFixed(0)}K`;
          action = language === 'fr' ? `price:de plus de ${lower.toLocaleString()} $` : `price:over $${lower.toLocaleString()}`;
          inventoryFilter = {
            field: 'Final Price',
            value: lower.toString(),
            operator: 'greaterThan'
          };
        } else {
          title = language === 'fr' 
            ? `${(lower / 1000).toFixed(0)}K$-${(upper / 1000).toFixed(0)}K$`
            : `$${(lower / 1000).toFixed(0)}K-$${(upper / 1000).toFixed(0)}K`;
          action = language === 'fr'
            ? `price:entre ${lower.toLocaleString()} $ et ${upper.toLocaleString()} $`
            : `price:between $${lower.toLocaleString()} and $${upper.toLocaleString()}`;
          inventoryFilter = {
            field: 'Final Price',
            value: `${lower},${upper}`,
            operator: 'between'
          };
        }
        
        priceRanges.push({
          id: `price-${i}`,
          position: 1000 + i,
          title,
          action,
          titleWordPos: 9999,
          inventoryFilter
        });
      }
      
      childrenToShow = [...childrenToShow, ...priceRanges];
    }

    const buttons = childrenToShow
      .filter(item => item != null)
      .map(item => ({
        position: item.position,
        title: item.title,
        level: currentLevel,
        icon: item.icon,
        clickAction: item.action || '',
        count: item.inventoryFilter ? getInventoryCountForItem(item, selection.positions) : undefined,
        hideIfZeroCount: item.hideIfZeroCount // Pass through the per-item setting
      }))
      .sort((a, b) => a.position - b.position);
    
    // Filter out items with zero count if their hideIfZeroCount flag is true
    const filtered = buttons.filter(btn => {
      logger.log(`🔍 Button "${btn.title}" (level ${btn.level}): hideIfZeroCount=${btn.hideIfZeroCount}, count=${btn.count}`);
      // If hideIfZeroCount is true and count is 0, hide the item
      if (btn.hideIfZeroCount === true && btn.count !== undefined && btn.count === 0) {
        logger.log(`  ❌ Hiding "${btn.title}" because hideIfZeroCount is true and count is 0`);
        return false;
      }
      return true;
    });
    logger.log(`📊 Level ${currentLevel} buttons after filtering:`, filtered.map(b => ({ title: b.title, count: b.count, hide: b.hideIfZeroCount })));
    return filtered;
  };

  // Store current inventory filter for message correlation
  const currentInventoryFilterRef = useRef<MenuItem['inventoryFilter'] | null>(null);
  const currentL1TitleRef = useRef<string | null>(null);

  // Handle button click
  const handleButtonClick = async (button: LevelButton): Promise<string | void> => {
    // Signal that prompt menu was clicked (for ChatInput to clear simulated text flag)
    window.dispatchEvent(new CustomEvent('promptMenuClick'));
    
    // Track usage using secure database function
    try {
      // Ensure session is initialized before tracking
      let currentSession = sessionManager.getCurrentSession();
      
      // If no session exists, initialize one
      if (!currentSession) {
        logger.log('⚠️ No session found when clicking prompt menu, initializing...');
        currentSession = sessionManager.initializeSession();
      }
      
      const sessionId = currentSession?.sessionId;
      
      if (!sessionId) {
        console.error('❌ Could not get session ID for prompt menu tracking');
      }
      
      // Track usage - Position-based tracking
      // Build positions array for tracking (legacy compatibility)
      const trackingPositions = [...selection.positions];
      if (trackingPositions.length < button.level - 1) {
        // Pad with zeros if needed
        while (trackingPositions.length < button.level - 1) {
          trackingPositions.push(0);
        }
      }
      trackingPositions.push(button.position);

      const isAutoSkipped = autoSkippedL1Ref.current;
      const effectiveLevel = isAutoSkipped ? button.level - 1 : button.level;

      await securedSupabase.raw.rpc('track_prompt_menu_usage', {
        p_dealer_id: effectiveDealerId,
        p_l1_position: trackingPositions[0] || 0,
        p_title_clicked: button.title,
        p_level_clicked: effectiveLevel,
        p_l2_position: trackingPositions[1] || undefined,
        p_l3_position: trackingPositions[2] || undefined,
        p_session_id: sessionId,
        p_user_agent: navigator.userAgent,
        p_auto_skipped_l1: isAutoSkipped
      });
    } catch (error) {
      console.error('Error tracking prompt menu usage:', error);
    }

    // Track GA4 event
    ga4Tracker.trackChatInteraction({
      interaction_type: 'prompt_menu_button_click',
      message_type: `level_${button.level}_${button.title}`,
      form_type: 'prompt_menu'
    });

    // Check for text override or other actions
    let currentMessageOverride: string | null = null;
    let actionResult: ClickActionResult | null = null;
    
    if (button.clickAction) {
      actionResult = processClickAction(button.clickAction);
      
      if (actionResult.type === 'javascript') {
        logger.log('🔄 Executing JavaScript action:', actionResult.value);
        if (actionResult.value) {
          executeSecureAction(actionResult.value);
        }
        return '';
      }
      
      if (actionResult.type === 'url') {
        // Securely redirect to URL after validation
        if (actionResult.value) {
          logger.log('🌐 Attempting redirect to URL:', actionResult.value);
          const success = safeRedirect(actionResult.value, createDealerURLConfig());
          if (!success) {
            console.error('🚫 Redirect blocked for security reasons:', actionResult.value);
          }
        }
        logger.log('🗑️ Returning CLEAR_MESSAGE_BOX for URL action');
        return 'CLEAR_MESSAGE_BOX';
      }
      
      if (actionResult.type === 'text') {
        // Check if this is a price range action (appends) or text override (replaces)
        const isPriceRange = button.clickAction.startsWith('price:');
        
        if (isPriceRange) {
          // For price ranges, append the full action text at the end
          const parentPositions = [...selection.positions]; // Don't include current price button
          
          // Build base message from parent path (without the price range)
          let baseMessage = '';
          if (parentPositions.length > 0) {
            const parentItem = getItemAtPath(parentPositions);
            // Priority 1: Check for messageTextOverride on parent
            if (parentItem?.messageTextOverride) {
              baseMessage = parentItem.messageTextOverride;
            } 
            // Priority 2: Build from selection
            else {
              const prefix = language === 'fr' ? "Je recherche" : "I'm looking for a";
              const fullMessage = buildMessageFromSelection({ positions: parentPositions });
              baseMessage = fullMessage.replace(prefix, '').trim();
            }
          }
          
          // Append full price range text (e.g., "between $50,000 and $60,000") at the end
          currentMessageOverride = `${baseMessage} ${actionResult.value}`.trim();
        } else {
          // Regular text override replaces the entire message
          currentMessageOverride = actionResult.value || null;
        }
      }
      
      if (actionResult.type === 'form') {
        // Signal to expand form and clear message box
        const formValue = actionResult.value;
        logger.log('📝 FORM ACTION DETECTED:', { 
          menuId: menuData?.prompt_menu_id,
          formType: formValue,
          buttonTitle: button.title,
          buttonLevel: button.level,
          rawAction: button.clickAction
        });
        return `FORM_EXPAND:${formValue}`;
      }
    }

    // Update selection - add this level's position to the array
    const newPositions = [...selection.positions];
    
    // If clicking at the current level, just add the position
    if (button.level === newPositions.length + 1) {
      newPositions.push(button.position);
    } 
    // If clicking back at a previous level, truncate and update
    else if (button.level <= newPositions.length) {
      newPositions.splice(button.level - 1, newPositions.length - button.level + 1, button.position);
    }

    // Track L1 title for showroom filtering
    let l1Title: string | undefined = undefined;
    if (button.level === 1) {
      l1Title = button.title;
    } else if (selection.l1Title && button.level > 1) {
      // Preserve existing L1 title when navigating deeper levels
      l1Title = selection.l1Title;
    }

    setSelection({ positions: newPositions, l1Title });

    // Check if there are more levels available
    const selectedItem = getItemAtPath(newPositions);
    const hasNextLevel = selectedItem && selectedItem.children && selectedItem.children.length > 0;
    
    // Store ACCUMULATED inventory filters - collect from all parent levels
    const accumulatedFilters: MenuItem['inventoryFilter'][] = [];
    let items = getCurrentItems();
    
    for (let i = 0; i < newPositions.length; i++) {
      const position = newPositions[i];
      const item = items.find(it => it.position === position);
      if (!item) break;
      
      if (item.inventoryFilter) {
        accumulatedFilters.push(item.inventoryFilter);
      }
      
      items = item.children || [];
    }
    
    // Store accumulated filters (all filters from parent path)
    if (accumulatedFilters.length > 0) {
      currentInventoryFilterRef.current = accumulatedFilters as any;
      sessionStorage.setItem('currentInventoryFilter', JSON.stringify(accumulatedFilters));
    } else {
      currentInventoryFilterRef.current = null;
      sessionStorage.removeItem('currentInventoryFilter');
    }
    
    // Store L1 title for showroom filtering
    if (l1Title) {
      currentL1TitleRef.current = l1Title;
      sessionStorage.setItem('currentL1Title', l1Title);
      logger.log('🏢 Stored L1 title for showroom:', l1Title);
    }
    
    // If message override exists, use it directly and store it in state
    if (currentMessageOverride) {
      setMessageOverride(currentMessageOverride);
      logger.log('📝 Final message (override):', currentMessageOverride);
      return currentMessageOverride;
    }
    
    // If no next level or action type is not continue, build and return message
    if (!hasNextLevel || actionResult?.type !== 'continue') {
      setMessageOverride(null);
      const message = buildMessageFromSelection({ positions: newPositions });
      return message;
    }

    // Otherwise just update selection for next level
    return '';
  };

  // Build message text from selection using word positions
  const buildMessageFromSelection = (sel: PromptMenuSelection): string => {
    if (sel.positions.length === 0) return '';
    
    const prefix = language === 'fr' ? "Je recherche" : "I'm looking for a";
    
    // Collect words with their positions by traversing the path
    const words: { word: string; position: number }[] = [];
    let items = getCurrentItems();
    let lastItem: MenuItem | null = null;
    let lastOverride: string | null = null;

    for (let i = 0; i < sel.positions.length; i++) {
      const position = sel.positions[i];
      const item = items.find(it => it.position === position);
      if (!item) break;

      lastItem = item;
      
      // Track the most recent (most specific) messageTextOverride found
      if (item.messageTextOverride) {
        lastOverride = item.messageTextOverride;
      }

      words.push({
        word: item.title,
        position: item.titleWordPos || (i + 1)
      });

      items = item.children || [];
    }

    // If we found any override, use it as-is (no prefix)
    if (lastOverride) {
      return lastOverride;
    }

    // Sort by word position and join
    const sortedWords = words.sort((a, b) => a.position - b.position);
    const messageText = sortedWords.map(w => w.word).join(' ');
    
    return `${prefix} ${messageText}`;
  };

  // Build message text from current selection
  const buildMessageText = (): string => {
    // If we have a message override, use it as-is (no prefix)
    if (messageOverride) {
      return messageOverride;
    }
    
    return buildMessageFromSelection(selection);
  };

  // Reset menu
  const resetMenu = () => {
    setSelection({ positions: [] });
    setMessageOverride(null);
    autoSkippedL1Ref.current = false;
  };

  // Go back
  const canGoBack = selection.positions.length > 0;
  const goBack = () => {
    if (canGoBack) {
      // Remove the last position
      const newPositions = [...selection.positions];
      newPositions.pop();
      
      // If going back to L1 (empty positions), clear auto-skip flag
      if (newPositions.length === 0) {
        autoSkippedL1Ref.current = false;
      }
      
      setSelection({ positions: newPositions });
      
      // Clear message override when going back
      setMessageOverride(null);
    }
  };

  const currentLevel = selection.positions.length + 1;

  return {
    isEnabled,
    isLoading,
    currentLevelItems: getCurrentLevelItems(),
    currentLevel,
    handleButtonClick,
    buildMessageText,
    resetMenu,
    canGoBack,
    goBack,
    language,
    refreshPromptMenu,
    getCurrentInventoryFilter: () => {
      // Try to get from ref first, then fallback to sessionStorage
      if (currentInventoryFilterRef.current) {
        return currentInventoryFilterRef.current;
      }
      const stored = sessionStorage.getItem('currentInventoryFilter');
      return stored ? JSON.parse(stored) : null;
    },
    getCurrentL1Title: () => {
      // Try to get from ref first, then fallback to sessionStorage
      if (currentL1TitleRef.current) {
        return currentL1TitleRef.current;
      }
      const stored = sessionStorage.getItem('currentL1Title');
      return stored || null;
    },
    getShowroomFilter: () => {
      // Convert accumulated inventory filters to showroom filter format
      // Showroom is only for "New" vehicles
      const inventoryFilters = currentInventoryFilterRef.current || 
        (sessionStorage.getItem('currentInventoryFilter') ? JSON.parse(sessionStorage.getItem('currentInventoryFilter')!) : null);
      
      if (!inventoryFilters || !Array.isArray(inventoryFilters)) {
        return null;
      }
      
      const showroomFilter: any = {};
      
      for (const filter of inventoryFilters) {
        const field = filter.field?.toLowerCase() || '';
        const value = filter.value;
        const operator = filter.operator || 'equals';
        
        // Map Vehicle Type
        if (field === 'vehicle type' || field === 'vehicletype' || field === 'vehicle_type') {
          showroomFilter.vehicleType = value;
        }
        
        // Map Fuel Type
        if (field === 'fuel_type' || field === 'fuel type' || field === 'fueltype') {
          showroomFilter.fuelType = value;
        }
        
        // Map Price ranges
        if (field === 'final price' || field === 'price') {
          if (operator === 'lessThan') {
            showroomFilter.maxPrice = parseFloat(value);
          } else if (operator === 'greaterThan') {
            showroomFilter.minPrice = parseFloat(value);
          } else if (operator === 'between') {
            const [min, max] = value.split(',').map((v: string) => parseFloat(v.trim()));
            showroomFilter.minPrice = min;
            showroomFilter.maxPrice = max;
          }
        }
      }
      
      // Only return if we have at least one filter
      return Object.keys(showroomFilter).length > 0 ? showroomFilter : null;
    }
  };
};
